import { ArrowLeftOutlined } from '@ant-design/icons';
import { type BookingStatus, PERMISSIONS } from '@navicosa/shared-types';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  App,
  Button,
  Card,
  Col,
  Descriptions,
  Input,
  Modal,
  Row,
  Select,
  Space,
  Spin,
  Table,
  Tag,
  Timeline,
  Typography,
} from 'antd';
import dayjs from 'dayjs';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api, errorText } from '../../api/client';
import {
  type AvailableEquipment,
  BOOKING_STATUS_COLOR,
  BOOKING_STATUS_LABEL,
  type BookingDetail,
  phoneLocal,
  type Supplier,
  vnd,
} from '../../api/types';
import { useAuth } from '../../auth/AuthContext';

const fmt = (d: string | null) => (d ? dayjs(d).format('DD/MM/YYYY') : '—');

export function BookingDetailPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const qc = useQueryClient();
  const { message } = App.useApp();
  const { can } = useAuth();
  const canManage = can(PERMISSIONS.BOOKINGS_MANAGE);
  const [assigning, setAssigning] = useState<BookingDetail['items'][number] | null>(null);
  const [pick, setPick] = useState<string>();
  const [moving, setMoving] = useState<BookingStatus | null>(null);
  const [note, setNote] = useState('');

  const q = useQuery({
    queryKey: ['booking', id],
    queryFn: () => api.get<BookingDetail>(`/admin/bookings/${id}`),
  });
  const b = q.data;
  const win = b
    ? { from: b.delivery_at ?? b.rental_start_at, to: b.pickup_at ?? b.rental_end_at }
    : null;
  const avail = useQuery({
    queryKey: ['available', assigning?.product_id, win?.from, win?.to, id],
    queryFn: () =>
      api.get<AvailableEquipment[]>('/admin/equipment-available', {
        product_id: assigning!.product_id,
        from: win!.from,
        to: win!.to!,
        exclude_booking_id: id,
      }),
    enabled: !!assigning && !!win?.to,
  });
  const suppliers = useQuery({
    queryKey: ['suppliers-pick'],
    queryFn: async () => (await api.page<Supplier>('/admin/suppliers', { limit: 100 })).items,
    enabled: !!assigning && can(PERMISSIONS.SUPPLIERS_READ),
  });

  if (q.isLoading) return <Spin />;
  if (!b) return <Alert type="error" message="Không tìm thấy đơn thuê" />;

  const run = async (fn: () => Promise<BookingDetail>, ok: string) => {
    try {
      qc.setQueryData(['booking', id], await fn());
      void qc.invalidateQueries({ queryKey: ['bookings'] });
      void qc.invalidateQueries({ queryKey: ['schedule'] });
      message.success(ok);
      return true;
    } catch (e) {
      message.error(errorText(e));
      return false;
    }
  };
  const unassigned = b.items.filter((i) => !i.equipment).length;

  return (
    <>
      <Space style={{ marginBottom: 16 }} wrap>
        <Button icon={<ArrowLeftOutlined />} onClick={() => nav(-1)}>
          Quay lại
        </Button>
        <Typography.Title level={4} style={{ margin: 0 }}>
          Đơn thuê {b.booking_code}
        </Typography.Title>
        <Tag color={BOOKING_STATUS_COLOR[b.status]}>{BOOKING_STATUS_LABEL[b.status]}</Tag>
      </Space>
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={16}>
          <Card title="Thông tin">
            <Descriptions
              column={{ xs: 1, sm: 1, md: 2, lg: 2, xl: 2, xxl: 2 }}
              size="small"
              bordered
            >
              <Descriptions.Item label="Khách">
                {[b.customer.company_name, b.customer.full_name].filter(Boolean).join(' – ')}
              </Descriptions.Item>
              <Descriptions.Item label="SĐT">{phoneLocal(b.customer.phone)}</Descriptions.Item>
              <Descriptions.Item label="Công trình" span={2}>
                {b.quotation.request.delivery_address}
                {b.quotation.request.location ? ` (${b.quotation.request.location.name})` : ''}
              </Descriptions.Item>
              <Descriptions.Item label="Giao xe">{fmt(b.delivery_at)}</Descriptions.Item>
              <Descriptions.Item label="Thu hồi">{fmt(b.pickup_at)}</Descriptions.Item>
              <Descriptions.Item label="Tính tiền">
                {fmt(b.rental_start_at)} đến {fmt(b.rental_end_at)}
              </Descriptions.Item>
              <Descriptions.Item label="Giá trị đơn">{vnd(b.total_amount)}</Descriptions.Item>
              <Descriptions.Item label="Báo giá">
                <Link to={`/quotations/${b.quotation.id}`}>{b.quotation.quote_code}</Link>
              </Descriptions.Item>
              <Descriptions.Item label="Yêu cầu">
                <Link to={`/requests/${b.quotation.request.id}`}>
                  {b.quotation.request.request_code}
                </Link>
              </Descriptions.Item>
            </Descriptions>
          </Card>
          <Card title="Xe trong đơn" style={{ marginTop: 16 }}>
            {unassigned > 0 && (
              <Alert
                style={{ marginBottom: 12 }}
                type="warning"
                showIcon
                message={`${unassigned} xe chưa gán xe cụ thể – phải gán trước khi chuyển "Đang giao xe" (CR-10)`}
              />
            )}
            <Table
              size="small"
              rowKey="id"
              pagination={false}
              dataSource={b.items}
              columns={[
                { title: 'Thiết bị', render: (_, i) => i.product.name },
                {
                  title: 'Xe',
                  render: (_, i) =>
                    i.equipment ? (
                      <Space size={4}>
                        <b>{i.equipment.equipment_code}</b>
                        {i.equipment.model && <span>{i.equipment.model}</span>}
                        {i.equipment.ownership_type === 'partner' && (
                          <Tag color="purple">Đối tác</Tag>
                        )}
                      </Space>
                    ) : (
                      <Tag color="orange">Chưa gán xe</Tag>
                    ),
                },
                {
                  title: 'Nguồn',
                  render: (_, i) =>
                    i.supplier ? i.supplier.name : i.from_partner ? 'Đối tác' : 'Navicosa',
                },
                {
                  title: 'Giữ lịch',
                  render: (_, i) =>
                    i.blocks.length
                      ? `${fmt(i.blocks[0]!.start_at)} – ${fmt(i.blocks[0]!.end_at)}`
                      : '—',
                },
                ...(canManage && b.can_assign
                  ? [
                      {
                        title: '',
                        render: (_: unknown, i: BookingDetail['items'][number]) => (
                          <Button
                            size="small"
                            onClick={() => (setAssigning(i), setPick(undefined))}
                          >
                            {i.equipment ? 'Đổi xe' : 'Gán xe'}
                          </Button>
                        ),
                      },
                    ]
                  : []),
              ]}
            />
          </Card>
        </Col>
        <Col xs={24} lg={8}>
          <Card title="Trạng thái">
            {canManage && b.allowed_transitions.length > 0 ? (
              <Space wrap>
                {b.allowed_transitions.map((s) => (
                  <Button
                    key={s}
                    type={s === 'cancelled' ? 'default' : 'primary'}
                    danger={s === 'cancelled'}
                    onClick={() => (setMoving(s), setNote(''))}
                  >
                    {BOOKING_STATUS_LABEL[s]}
                  </Button>
                ))}
              </Space>
            ) : (
              <Typography.Text type="secondary">Không có thao tác</Typography.Text>
            )}
          </Card>
          <Card title="Lịch sử" style={{ marginTop: 16 }}>
            <Timeline
              items={b.status_history.map((h) => ({
                children: (
                  <>
                    <b>{BOOKING_STATUS_LABEL[h.to_status]}</b>
                    <div style={{ fontSize: 12, color: '#737B86' }}>
                      {dayjs(h.created_at).format('DD/MM/YYYY HH:mm')} · {h.user?.full_name ?? '—'}
                    </div>
                    {h.note && <div>{h.note}</div>}
                  </>
                ),
              }))}
            />
          </Card>
        </Col>
      </Row>

      <Modal
        open={!!assigning}
        title={`${assigning?.equipment ? 'Đổi' : 'Gán'} xe – ${assigning?.product.name ?? ''}`}
        okText="Lưu"
        okButtonProps={{ disabled: !pick }}
        onCancel={() => setAssigning(null)}
        onOk={async () => {
          if (!assigning || !pick) return;
          const body = pick.startsWith('eq:')
            ? { equipment_id: pick.slice(3) }
            : { supplier_id: pick.slice(4) };
          if (
            await run(
              () => api.patch(`/admin/bookings/${id}/items/${assigning.id}`, body),
              'Đã cập nhật xe',
            )
          )
            setAssigning(null);
        }}
      >
        {!win?.to && (
          <Alert
            type="warning"
            showIcon
            message="Đơn chưa có ngày kết thúc/thu hồi nên chưa giữ lịch xe cụ thể được."
          />
        )}
        <Select
          style={{ width: '100%' }}
          showSearch
          optionFilterProp="label"
          placeholder="Chọn xe còn trống hoặc đối tác"
          value={pick}
          onChange={setPick}
          loading={avail.isLoading}
          options={[
            {
              label: 'Xe còn trống trong thời gian thuê',
              options: (avail.data ?? []).map((e) => ({
                value: `eq:${e.id}`,
                label: `${e.equipment_code}${e.model ? ` · ${e.model}` : ''}${e.ownership_type === 'partner' ? ' (đối tác)' : ''}${e.location ? ` · ${e.location.name}` : ''}`,
              })),
            },
            ...(suppliers.data?.length
              ? [
                  {
                    label: 'Đối tác – gán xe cụ thể sau',
                    options: suppliers.data.map((s) => ({ value: `sup:${s.id}`, label: s.name })),
                  },
                ]
              : []),
          ]}
        />
      </Modal>

      <Modal
        open={moving !== null}
        title={moving ? `Chuyển sang "${BOOKING_STATUS_LABEL[moving]}"` : ''}
        okText="Xác nhận"
        okButtonProps={{
          danger: moving === 'cancelled',
          disabled: moving === 'cancelled' && note.trim().length < 3,
        }}
        onCancel={() => setMoving(null)}
        onOk={async () => {
          if (
            moving &&
            (await run(
              () =>
                api.patch(`/admin/bookings/${id}/status`, {
                  status: moving,
                  note: note.trim() || null,
                }),
              'Đã cập nhật trạng thái',
            ))
          )
            setMoving(null);
        }}
      >
        {moving === 'cancelled' && (
          <Typography.Paragraph type="secondary">
            Hủy đơn sẽ nhả lịch của các xe trong đơn.
          </Typography.Paragraph>
        )}
        <Input.TextArea
          rows={3}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder={moving === 'cancelled' ? 'Lý do hủy (bắt buộc)' : 'Ghi chú (không bắt buộc)'}
        />
      </Modal>
    </>
  );
}
